import { FC, useRef, useContext, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import __ from "src/utils/Translations";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import createStructureIcon from "src/assets/custom/icons/create_structure.svg";
import useCreateStructure from "src/Pages/Company/OrganizationStructure/api/useCreateStructure";
import { StructureType } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/CreateStructure/createStructureType";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { organizationStructureStructureCreate } from "src/utils/RoleBasedSecurity/permissions";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import ImageUpload from "src/Components/ImageUpload/ImageUpload";
import { ImageCroppRef } from "src/Components/ImageCropp/ImageCropp";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import { useCompanyBreadcrumbs } from "src/Pages/Company/breadcrumbsHelper";
import { AxiosError } from "axios";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import { styles } from "./createStructureStyle";
import {
  createStructureScheme,
  ICreateStructure,
} from "./CreateStructure.scheme";

const CreateStructure: FC = () => {
  const companyId = useCompanyId();
  const hasPermissionStructureCreate = usePermission(
    organizationStructureStructureCreate
  );

  const { mutate, error: createStructureError } = useCreateStructure(companyId);

  const logStatus = useLogStatus();

  useEffect(() => {
    if (!createStructureError) return;
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "CreateStructure: createStructure failed",
      statusDetails: {
        source: "API",
        operation: "createStructure",
        status: (createStructureError as AxiosError)?.response?.status,
        url: (createStructureError as AxiosError)?.config?.url,
      },
      errorObject:
        createStructureError instanceof Error
          ? createStructureError
          : new Error(String(createStructureError)),
    });
  }, [createStructureError, logStatus]);

  const formMethods = useForm<ICreateStructure>({
    resolver: standardSchemaResolver(createStructureScheme),
    defaultValues: {
      name: "",
      type: StructureType.CREATE_STRUCTURE_TYPE,
      avatarBase64: null,
    },
    mode: "onChange",
  });

  const { control, handleSubmit, setValue, reset } = formMethods;

  const handleResetModal = () => {
    reset();
    resetModal();
  };

  const onSubmit = (formData: ICreateStructure) => {
    const payload = {
      name: formData.name,
      type: StructureType.CREATE_STRUCTURE_TYPE,
      avatarBase64: formData.avatarBase64,
    };
    mutate(payload);
  };

  const { setModal, resetModal } = useContext(GlobalModalContext);
  const cropperRef = useRef<ImageCroppRef>(null);

  const modalActions: {
    label: string;
    variant?: "outlined" | "text" | "contained";
    callbackFn: VoidFunction;
  }[] = [
    {
      label: __("Anuluj") as string,
      variant: "outlined",
      callbackFn: handleResetModal,
    },
    {
      label: __("Dodaj") as string,
      variant: "contained",
      callbackFn: async () => {
        await cropperRef.current?.submitCrop();
        await handleSubmit(onSubmit)();
        resetModal();
      },
    },
  ];

  const breadcrumbs = useCompanyBreadcrumbs([
    {
      label: __("Struktura organizacji"),
      path: "/organization-structure",
    },
    {
      label: __("Konfiguracja struktury"),
      path: "/organization-structure/configure-structure/create-structure",
    },
  ]);

  return (
    <>
      <BreadcrumbsMui items={breadcrumbs} sx={{ marginBottom: "32px" }} />
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: "fontWeightBold",
            textTransform: "uppercase",
            mb: "32px",
          }}
        >
          {`${__("Tworzenie struktury")}`}
        </Typography>
        <Box sx={styles.container}>
          <Box sx={styles.iconContainer}>
            <img src={createStructureIcon} alt={__("tworzenie struktury")} />
          </Box>
          <Typography sx={styles.title}>
            {__("Stwórz strukturę dla firmy")}
          </Typography>
          <Typography sx={styles.description}>
            {__(
              "Struktura będzie obejmować wszystkich pracowników w Twojej firmie."
            )}
          </Typography>

          {hasPermissionStructureCreate && (
            <Button
              variant="contained"
              size="large"
              type="button"
              onClick={() => {
                setModal({
                  open: true,
                  title: (
                    <Typography sx={styles.dialogTitle} component="h2">
                      {__("Nowa struktura")}
                    </Typography>
                  ),
                  description: __(
                    "Struktura będzie widoczna dla wszystkich pracowników w Twojej organizacji."
                  ),
                  customElements: (
                    <>
                      <FormProvider {...formMethods}>
                        <Controller
                          name="name"
                          control={control}
                          render={({ field }) => (
                            <ControlledTextField
                              {...field}
                              label={__("Nazwa")}
                              sx={{ width: "100%" }}
                              showLimitOnError
                              maxLength={128}
                            />
                          )}
                        />
                        <Box sx={{ mt: 1, mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>
                            {__("Avatar struktury")}:
                          </Typography>
                          <ImageUpload
                            ref={cropperRef}
                            onImageCropped={(img) => {
                              const result =
                                typeof img === "string"
                                  ? img
                                  : URL.createObjectURL(img);
                              setValue("avatarBase64", result);
                            }}
                          />
                        </Box>
                      </FormProvider>
                    </>
                  ),
                  actions: modalActions,
                  maxWidth: 800,
                  disableExitButton: true,
                  disableOutsideClick: true,
                });
              }}
            >
              {__("Zaczynamy")}
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CreateStructure;
